import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SEEN_KEY = "cg_glimpse_popup_seen";
export const POPUP_DELAY_MS = 5000;

// Non-blocking invitation: no backdrop, so the page stays fully usable.
export const GlimpsePopup = () => {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!ready || user === null || sessionStorage.getItem(SEEN_KEY)) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (user) {
        if (user.role !== "participant") return;
        try {
          const { data } = await api.get("/glimpse/state");
          if (data.glimpse?.status === "completed" || data.full_assessment_in_progress) return;
        } catch { return; }
      }
      if (!cancelled) { sessionStorage.setItem(SEEN_KEY, "1"); setOpen(true); }
    }, POPUP_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [ready, user]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;
  return (
    <div role="dialog" aria-modal="false" aria-labelledby="glimpse-popup-title" data-testid="glimpse-popup"
      className="fixed z-[60] bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[420px] bg-[#F7F5F0] border border-[rgba(23,50,77,0.15)] rounded-2xl shadow-[0_24px_60px_rgba(16,24,32,0.25)] p-7 cg-fade-up">
      <button onClick={() => setOpen(false)} aria-label="Close" data-testid="glimpse-popup-close"
        className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-[#17324D] hover:bg-[rgba(23,50,77,0.08)] transition-colors">
        <X className="w-5 h-5" />
      </button>
      <p className="text-[11px] tracking-[0.18em] uppercase text-[#8A6A3B] mb-2">Free · 21 statements</p>
      <h2 id="glimpse-popup-title" className="font-editorial text-[28px] leading-tight text-[#101820] pr-8">Curious About Where Your Authority Lies?</h2>
      <p className="mt-3 text-[#292D32]">You make decisions every day. But what's influencing them?</p>
      <p className="mt-2 text-sm text-[#4A4F57]">Discover a little more about your relationship with personal authority with our free Authority Glimpse.</p>
      <p className="mt-2 text-sm text-[#4A4F57]">Answer 21 questions. Explore seven dimensions. Receive your personalized Glimpse Portrait.</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <button data-testid="glimpse-popup-try" onClick={() => { setOpen(false); navigate("/glimpse"); }}
          className="cg-ink-btn text-sm px-6 py-3 rounded-full">Try It for Free</button>
        <button data-testid="glimpse-popup-later" onClick={() => setOpen(false)}
          className="text-sm px-5 py-3 rounded-full text-[#17324D] border border-[rgba(23,50,77,0.25)] hover:bg-[rgba(23,50,77,0.06)] transition-colors">Maybe Later</button>
      </div>
    </div>
  );
};
